import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { NbButtonModule, NbCardModule, NbFormFieldModule, NbInputModule, NbSelectModule, NbSpinnerModule } from '@nebular/theme'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { EmailTemplate } from '../../interfaces/email-template.interface'
import { EmailComposerService } from '../../services/email-composer.service'

@Component({
  selector: 'ngx-action-editor',
  template: `
    <nb-card *ngIf="ready">
      <nb-card-header>
        <h3>{{ actionId ? 'Edit Action' : 'Create Action' }}</h3>
      </nb-card-header>
      <nb-card-body>
        <form [formGroup]="form" (ngSubmit)="save()">
          <nb-form-field class="w-100">
            <input nbInput placeholder="Action key" formControlName="action" />
          </nb-form-field>

          <nb-form-field class="w-100">
            <nb-select placeholder="Email template" formControlName="email_template_id">
              <nb-option *ngFor="let t of templates" [value]="t.id">{{ t.name }} — {{ t.subject }}</nb-option>
            </nb-select>
          </nb-form-field>

          <div class="actions">
            <button nbButton type="button" (click)="close.emit()">Back</button>
            <button nbButton status="primary" type="submit" [disabled]="form.invalid || saving">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </form>
      </nb-card-body>
    </nb-card>
  `,
  styles: [
    `
      .action-editor {
        padding: 16px;
      }
      .w-100 {
        width: 100%;
      }
      .actions {
        display: flex;
        gap: 8px;
        margin-top: 12px;
      }
    `
  ],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NbCardModule, NbButtonModule, NbFormFieldModule, NbInputModule, NbSelectModule, NbSpinnerModule]
})
export class ActionEditorComponent implements OnInit, OnDestroy {
  @Input() actionId: number | null = null
  @Output() close = new EventEmitter<void>()
  @Output() saved = new EventEmitter<any>()

  private destroy$ = new Subject<void>()
  ready = false
  saving = false
  form: UntypedFormGroup
  templates: EmailTemplate[] = []

  constructor(private fb: UntypedFormBuilder, private service: EmailComposerService) {
    this.form = this.fb.group({
      action: ['', [Validators.required, Validators.minLength(2)]],
      email_template_id: [null, [Validators.required]]
    })
  }

  ngOnInit(): void {
    // load templates for select
    this.service
      .getTemplates(undefined, true, undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => (this.templates = res.data || []))

    if (this.actionId) {
      this.service
        .getAction(this.actionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          const a = res.data
          this.form.patchValue({ action: a.action, email_template_id: a.email_template?.id })
          this.ready = true
        })
    } else {
      this.ready = true
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  save(): void {
    if (this.form.invalid) return
    this.saving = true
    const payload = this.form.value as { action: string; email_template_id: number }

    const obs = this.actionId ? this.service.updateAction(this.actionId, payload) : this.service.createAction(payload)
    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.saving = false
        this.saved.emit(res.data)
      },
      error: () => (this.saving = false)
    })
  }
}
